import React, {forwardRef, useMemo, useState} from 'react'
import PropTypes from 'prop-types'
import {useIntl} from 'react-intl'
import {
    Box,
    Button,
    Checkbox as ChakraCheckbox,
    Field as ChakraField,
    IconButton,
    Input as ChakraInput,
    InputGroup,
    useSlotRecipe
} from '@chakra-ui/react3'
import {CheckboxIcon, CheckIcon, EyeIcon, WarningIcon} from '../icons'
import inputRecipe from './index.recipe'
import {useController} from 'react-hook-form'
import {useMergeRefs} from '@chakra-ui/react-use-merge-refs'

/**
 * Leap input field built on top of Chakra Field and Input.
 *
 * Supports labels, helper and error text, optional and required markers,
 * start/end tokens, password visibility toggle, clearable search behavior,
 * and success or warning status icons.
 *
 * @param {Object} props
 * @param {string} [props.id] - Explicit input id. Falls back to a generated id.
 * @param {React.ReactNode} [props.label] - Accessible field label.
 * @param {boolean} [props.hideLabel=false] - Visually hides the label while keeping it accessible.
 * @param {'text'|'email'|'password'|'search'|'tel'|'number'|'checkbox'} [props.type='text'] - Native input type.
 * @param {React.ReactNode} [props.helperText] - Helper text linked through `aria-describedby`.
 * @param {React.ReactNode} [props.errorText] - Error text linked through `aria-describedby`.
 * @param {React.ReactNode} [props.optionalText] - Optional text shown next to the label when field is not required.
 * @param {'outline'|'filled'|'subtle'} [props.variant='outline'] - Visual input variant.
 * @param {boolean} [props.required=false] - Marks the field as required.
 * @param {boolean} [props.isRequired=false] - Legacy alias for `required`.
 * @param {boolean} [props.disabled=false] - Disables the field.
 * @param {boolean} [props.isDisabled=false] - Legacy alias for `disabled`.
 * @param {boolean} [props.invalid=false] - Forces invalid state even without `errorText`.
 * @param {boolean} [props.isInvalid=false] - Legacy alias for `invalid`.
 * @param {string} [props.placeholder] - Native input placeholder.
 * @param {React.ReactNode} [props.startElement] - Content rendered in the start slot.
 * @param {React.ReactNode|Function} [props.endElement] - Content rendered in the end slot. Overrides built-in status and action elements.
 * @param {Object} [props.startElementProps] - Additional props for the start slot wrapper.
 * @param {Object} [props.endElementProps] - Additional props for the end slot wrapper.
 * @param {string} [props.startOffset] - Space deducted from the automatic input start padding.
 * @param {boolean} [props.clearable=false] - Enables clear action for search input with a current value.
 * @param {Function} [props.onClear] - Callback fired when clearable search input is cleared.
 * @param {Function} [props.onCheckedChange] - Chakra checkbox change handler for `type="checkbox"`.
 * @param {'success'|'warning'} [props.status] - Non-error validation status.
 * @param {string} [props.statusLabel] - Accessible label for status icons.
 * @param {Function} [props.onChange] - Change handler for input or checkbox-like event payload.
 * @param {string|number|boolean} [props.defaultValue] - Initial field value.
 * @param {Object} [props.inputProps] - Additional props for `Checkbox.HiddenInput` when `type="checkbox"`.
 * @param {React.Ref<HTMLInputElement>} ref - Forwarded input ref.
 * @returns {React.ReactElement}
 */
export const InputField = forwardRef(function InputField(
    {
        id,
        name,
        control,
        rules = {},
        label,
        hideLabel = false,
        type = 'text',
        helperText,
        errorText,
        optionalText,
        variant = 'outline',
        required = false,
        isRequired = false,
        disabled = false,
        isDisabled = false,
        invalid = false,
        isInvalid = false,
        placeholder,
        startElement,
        endElement,
        startElementProps,
        endElementProps,
        startOffset,
        clearable = false,
        onClear,
        onCheckedChange,
        status,
        statusLabel,
        onChange,
        defaultValue,
        inputProps,
        ...nativeInputProps
    },
    ref
) {
    const {formatMessage} = useIntl()
    const [isPasswordVisible, setPasswordVisible] = useState(false)
    const isCheckbox = type === 'checkbox'
    const isPassword = type === 'password'
    const resolvedRequired = required || isRequired
    const resolvedDisabled = disabled || isDisabled
    const resolvedInvalid = invalid || isInvalid
    const resolvedType = isPassword && isPasswordVisible ? 'text' : type
    const recipe = useSlotRecipe({recipe: inputRecipe})
    const {
        field: {ref: fieldRef, value, onChange: fieldOnChange, name: fieldName},
        fieldState
    } = useController({
        name,
        control,
        rules,
        defaultValue: isCheckbox ? Boolean(defaultValue) : defaultValue,
        disabled: resolvedDisabled
    })
    const combinedRef = useMergeRefs(fieldRef, ref)
    const resolvedErrorText = errorText ?? fieldState.error?.message
    const hasError = resolvedInvalid || fieldState.invalid || Boolean(resolvedErrorText)
    const styles = useMemo(() => recipe({disabled: resolvedDisabled}), [recipe, resolvedDisabled])
    const invalidInputLabel =
        statusLabel ||
        formatMessage({
            id: 'global.invalid_input',
            defaultMessage: 'Invalid input'
        })
    const validInputLabel =
        statusLabel ||
        formatMessage({
            id: 'global.valid_input',
            defaultMessage: 'Valid input'
        })
    const warningLabel =
        statusLabel ||
        formatMessage({
            id: 'global.warning',
            defaultMessage: 'Warning'
        })

    const handleValueChange = (nextValue, event) => {
        fieldOnChange(nextValue)

        onChange?.(event)
    }

    const handleCheckedChange = (details) => {
        const nextChecked = details.checked === true

        fieldOnChange(nextChecked)
        onCheckedChange?.(details)
        onChange?.({
            target: {
                checked: nextChecked,
                id,
                name: fieldName,
                value: nativeInputProps.value
            }
        })
    }

    const renderEndElement = () => {
        if (typeof endElement === 'function') {
            return endElement({value: value ?? '', invalid: hasError, disabled})
        }

        if (endElement) return endElement

        if (hasError) {
            return (
                <InputToken srLabel={invalidInputLabel}>
                    <WarningIcon {...styles.icon} {...styles.errorIcon} />
                </InputToken>
            )
        }

        if (clearable && type === 'search' && value) {
            return (
                <Button
                    {...styles.deleteButton}
                    aria-label={formatMessage({
                        defaultMessage: 'Delete input',
                        id: 'product_list.search.delete_input'
                    })}
                    onClick={() => {
                        fieldOnChange('')
                        onClear?.()
                    }}
                >
                    {formatMessage({
                        defaultMessage: 'Delete',
                        id: 'global.delete'
                    })}
                </Button>
            )
        }

        if (isPassword) {
            return (
                <IconButton
                    {...styles.iconButton}
                    aria-label={
                        isPasswordVisible
                            ? formatMessage({
                                  id: 'global.hide_password',
                                  defaultMessage: 'Hide password'
                              })
                            : formatMessage({
                                  id: 'global.show_password',
                                  defaultMessage: 'Show password'
                              })
                    }
                    aria-pressed={isPasswordVisible}
                    onClick={() => setPasswordVisible((visible) => !visible)}
                >
                    {isPasswordVisible ? (
                        <EyeIcon {...styles.icon} />
                    ) : (
                        <EyeIcon {...styles.icon} />
                    )}
                </IconButton>
            )
        }

        if (status === 'success') {
            return (
                <InputToken srLabel={validInputLabel}>
                    <CheckIcon {...styles.icon} />
                </InputToken>
            )
        }

        if (status === 'warning') {
            return (
                <InputToken srLabel={warningLabel}>
                    <WarningIcon {...styles.icon} />
                </InputToken>
            )
        }

        return null
    }

    return (
        <ChakraField.Root
            id={id}
            required={resolvedRequired}
            invalid={hasError}
            disabled={resolvedDisabled}
            variant={type === 'checkbox' ? 'checkbox' : undefined}
        >
            {type !== 'checkbox' && label ? (
                <ChakraField.Label className={hideLabel ? 'sr-only' : undefined}>
                    {label}
                    <ChakraField.RequiredIndicator />
                    {!resolvedRequired && optionalText ? (
                        <Box as="span" marginInlineStart="spacing-4">
                            {optionalText}
                        </Box>
                    ) : null}
                </ChakraField.Label>
            ) : null}
            {type === 'checkbox' && (
                <ChakraCheckbox.Root
                    {...nativeInputProps}
                    name={fieldName}
                    checked={Boolean(value)}
                    disabled={resolvedDisabled}
                    invalid={hasError}
                    required={resolvedRequired}
                    onCheckedChange={handleCheckedChange}
                >
                    <ChakraCheckbox.HiddenInput {...inputProps} ref={combinedRef} />
                    <ChakraCheckbox.Control>
                        <ChakraCheckbox.Indicator checked={<CheckboxIcon aria-hidden />} />
                    </ChakraCheckbox.Control>
                    {label ? (
                        <ChakraCheckbox.Label className={hideLabel ? 'sr-only' : undefined}>
                            {label}
                            {!resolvedRequired && optionalText ? (
                                <Box as="span" marginInlineStart="spacing-4">
                                    {optionalText}
                                </Box>
                            ) : null}
                        </ChakraCheckbox.Label>
                    ) : null}
                </ChakraCheckbox.Root>
            )}
            {type !== 'checkbox' && (
                <InputGroup
                    startElement={startElement}
                    startOffset={startOffset}
                    endElement={renderEndElement()}
                    startElementProps={{
                        ...styles.startElement,
                        ...startElementProps
                    }}
                    endElementProps={{
                        ...styles.endElement,
                        pointerEvents: endElement ? 'auto' : undefined,
                        ...endElementProps
                    }}
                    css={styles.group}
                >
                    <ChakraInput
                        {...nativeInputProps}
                        ref={combinedRef}
                        type={resolvedType}
                        placeholder={placeholder}
                        variant={variant}
                        value={value ?? ''}
                        disabled={resolvedDisabled}
                        onChange={(event) => handleValueChange(event.target.value, event)}
                        // Suppress the focus-visible ring when the user clicks with a mouse.
                        // Browsers show :focus-visible on click for inputs (unlike buttons), so we
                        // track mouse intent via a data attribute and strip the outline only then —
                        // keyboard users still get the full focus ring.
                        onMouseDown={(e) => {
                            e.currentTarget.setAttribute('data-mouse-focused', 'true')
                            nativeInputProps.onMouseDown?.(e)
                        }}
                        onBlur={(e) => {
                            e.currentTarget.removeAttribute('data-mouse-focused')
                            nativeInputProps.onBlur?.(e)
                        }}
                        onKeyDown={(e) => {
                            e.currentTarget.removeAttribute('data-mouse-focused')
                            nativeInputProps.onKeyDown?.(e)
                        }}
                    />
                </InputGroup>
            )}
            {resolvedErrorText && hasError ? (
                <ChakraField.ErrorText>{resolvedErrorText}</ChakraField.ErrorText>
            ) : null}
            {helperText ? <ChakraField.HelperText>{helperText}</ChakraField.HelperText> : null}
        </ChakraField.Root>
    )
})

InputField.propTypes = {
    id: PropTypes.string,
    name: PropTypes.string,
    control: PropTypes.object,
    rules: PropTypes.object,
    label: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    hideLabel: PropTypes.bool,
    type: PropTypes.oneOf(['text', 'email', 'password', 'search', 'tel', 'number', 'checkbox']),
    helperText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    errorText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    optionalText: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    variant: PropTypes.oneOf(['outline', 'filled', 'subtle']),
    required: PropTypes.bool,
    isRequired: PropTypes.bool,
    disabled: PropTypes.bool,
    isDisabled: PropTypes.bool,
    invalid: PropTypes.bool,
    isInvalid: PropTypes.bool,
    placeholder: PropTypes.string,
    startElement: PropTypes.node,
    endElement: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
    startElementProps: PropTypes.object,
    endElementProps: PropTypes.object,
    startOffset: PropTypes.string,
    clearable: PropTypes.bool,
    onClear: PropTypes.func,
    onCheckedChange: PropTypes.func,
    status: PropTypes.oneOf(['success', 'warning']),
    statusLabel: PropTypes.string,
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    defaultValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number, PropTypes.bool]),
    onChange: PropTypes.func,
    inputProps: PropTypes.object
}

export const InputToken = ({children, srLabel = ''}) => {
    const recipe = useSlotRecipe({recipe: inputRecipe})
    const styles = recipe()

    if (!children) return null

    const content = React.isValidElement(children)
        ? React.cloneElement(children, {
              ...styles.icon,
              ...children.props,
              'aria-hidden': srLabel ? true : children.props['aria-hidden']
          })
        : children

    return (
        <Box display="inline-flex" alignItems="center" justifyContent="center">
            {srLabel ? <Box className="sr-only">{srLabel}</Box> : null}
            <Box as="span">{content}</Box>
        </Box>
    )
}

InputToken.propTypes = {
    children: PropTypes.node,
    srLabel: PropTypes.string
}

export default InputField
